import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { createMove } from '@/api/moves'
import { movesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'
import type { CreateMoveDto } from '@/api/generated/api'

/**
 * Hook to create a new move
 *
 * Handles:
 * - API call to create move
 * - Cache invalidation for move lists
 * - Navigation to new move detail page on success
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const createMove = useCreateMove()
 *
 * const handleSubmit = async (data) => {
 *   await createMove.mutateAsync({
 *     name: data.name,
 *     description: data.description,
 *     url: data.url
 *   })
 * }
 * ```
 */
export function useCreateMove() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateMoveDto) => createMove(data),
    onSuccess: newMove => {
      // Invalidate all move list queries
      queryClient.invalidateQueries({ queryKey: movesKeys.lists() })

      // Show success toast
      toast({
        title: 'Move created',
        description: `"${newMove.name}" has been created successfully.`,
      })

      // Navigate to new move detail page
      navigate(`/moves/${newMove.id}`)
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to create move',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
